import { create } from "zustand";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "placement" | "worklog" | "course" | "payment" | "account" | "system";
  read: boolean;
  timestamp: string;
}

import { useAuthStore } from "./authStore";
import apiClient from "@/lib/api-client";
import { unwrapApiArray } from "@/lib/api-response";

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => void;
  markAllRead: () => void;
}

const mockNotifications: Notification[] = [
  {
    id: "1",
    title: "Placement Assigned",
    message: "You have been assigned to CRDB Bank for your field attachment.",
    type: "placement",
    read: false,
    timestamp: "2026-04-15T10:30:00",
  },
  {
    id: "2",
    title: "Work Log Approved",
    message: "Your work log for 14/04/2026 has been approved by your supervisor.",
    type: "worklog",
    read: false,
    timestamp: "2026-04-14T16:00:00",
  },
  {
    id: "3",
    title: "New Course Available",
    message: "Professional Communication Skills course is now open for enrollment.",
    type: "course",
    read: true,
    timestamp: "2026-04-13T09:00:00",
  },
  {
    id: "4",
    title: "Payment Confirmed",
    message: "Your registration fee of TZS 50,000 has been received.",
    type: "payment",
    read: true,
    timestamp: "2026-04-12T14:20:00",
  },
];

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  fetchNotifications: async () => {
    const user = useAuthStore.getState().user;
    if (!user) return;

    if (user.isDemo) {
      set({
        notifications: mockNotifications,
        unreadCount: mockNotifications.filter((n) => !n.read).length,
      });
      return;
    }

    set({ isLoading: true });
    try {
      const res = await apiClient.get("/notifications", {
        skipGlobalErrorHandler: true,
      } as any);
      const notifications = unwrapApiArray<any>(res).map((n) => ({
        id: String(n.id),
        title: n.title || "Notification",
        message: n.message || n.body || "",
        type: n.type || "system",
        read: Boolean(n.read ?? n.is_read),
        timestamp: n.timestamp || n.created_at || new Date().toISOString(),
      }));
      set({ notifications, unreadCount: notifications.filter((n) => !n.read).length });
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      set({ isLoading: false });
    }
  },
  markAsRead: async (id) => {
    const user = useAuthStore.getState().user;
    if (user && !user.isDemo) {
      await apiClient.post(`/notifications/${id}/read`);
    }
    set((state) => {
      const notifications = state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return { notifications, unreadCount: notifications.filter((n) => !n.read).length };
    });
  },
  markAllRead: async () => {
    const user = useAuthStore.getState().user;
    if (user && !user.isDemo) {
      await apiClient.post("/notifications/read-all");
    }
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },
}));
